<template>
  <div class="rm-screen">
    <div v-if="!selectedMealCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="failed" class="rm-empty">
      {{ $t("report_mobile.load_failed") }}
      <button type="button" class="rm-btn rm-btn--sm gm-retry" @click="fetchDailyMoreRestaurants">
        {{ $t("report_mobile.retry") }}
      </button>
    </div>
    <div v-else-if="loading.fetchDailyMoreRestaurants" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <template v-if="filters.search">{{ $t("report_mobile.no_results_for", { search: filters.search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_reserved_meals_data") }}
      </template>
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("r_o") }}</span>
          <span class="rm-stat__value rm-num">{{ mealTotal }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("companies") }}</span>
          <span class="rm-stat__value rm-num">{{ rows.length }}</span>
        </div>
      </div>

      <ul class="rm-list rm-card">
        <li v-for="row in rows" :key="row.key" class="rm-mrow rm-mrow--action">
          <button type="button" class="rm-mrow__btn" :disabled="!row.users.length" @click="openUsers(row)">
            <span class="rm-mrow__text">
              <span class="rm-mrow__main">
                <span class="rm-mrow__name">{{ row.name }}</span>
                <span class="rm-mrow__value rm-num">{{ row.total }}</span>
              </span>
              <span v-if="row.shifts.length" class="rm-mrow__shifts">
                <span v-for="shift in row.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                  {{ shift.quantity }}
                </span>
              </span>
            </span>
            <span class="rm-visually-hidden">{{ $t("show_employees_who_ordered") }}</span>
            <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
          </button>
        </li>
      </ul>
    </template>

    <ui-sheet v-model="usersSheet" :ready="Boolean(selectedRow)" :label="$t('show_employees_who_ordered')">
      <template #title>
        {{ selectedRow.name }}
        <small class="gm-sheet-sub">{{ $t("show_employees_who_ordered") }}</small>
      </template>
      <template #default>
        <ul class="rm-list rm-card">
          <li v-for="user in selectedUsers" :key="user.id" class="gm-wk">
            <span class="rm-avatar">{{ user.initials }}</span>
            <span class="gm-wk__text">
              <span class="gm-wk__name">{{ user.name }}</span>
              <span class="rm-muted gm-wk__meta rm-num">{{ user.meta }}</span>
            </span>
          </li>
        </ul>
      </template>
    </ui-sheet>
  </div>
</template>

<script>
import ReportMealsTabReservedMeals from "@/components/report/group-meals/ReportMealsTabReservedMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasGroupReservedFetch from "../../../shared/group/hasGroupReservedFetch";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import "../reportMobileMealCards.scss";

/**
 * Mobilni "Rezervni obroci" (jela iz grupa): firme sa brojem rezervnih obroka po smenama; klik na firmu otvara donju
 * karticu sa radnicima koji su naručili. Firme i period su u zaglavlju, pretraga u lupi. Nasleđuje štampu i Excel iz
 * starog taba; učitavanje je u hasGroupReservedFetch (ponavlja se i pri promeni firmi).
 */
export default {
  name: "GroupMobileReserved",
  extends: ReportMealsTabReservedMeals,
  mixins: [hasGroupReservedFetch],
  components: { ReportShiftTag },
  data: () => ({
    usersSheet: false,
    selectedRow: null,
  }),
  computed: {
    rows() {
      const named = this.companies.map((company, index) => ({
        key: `${company.id ?? index}-${index}`,
        name: this.toLatin(company.name),
        total: company.total || 0,
        users: Array.isArray(company.users) ? company.users : [],
        shifts: SHIFTS.map((shift) => ({ id: shift.id, quantity: company[shift.key] || 0 })).filter((shift) => shift.quantity > 0),
      }));
      const found = this.filters.search ? this.customSearch(named, [{ value: "name" }], this.filters.search) : named;
      return [...found].sort((a, b) => a.name.localeCompare(b.name));
    },
    mealTotal() {
      return this.rows.reduce((sum, row) => sum + row.total, 0);
    },
    selectedUsers() {
      return (this.selectedRow?.users || []).map((user) => ({
        id: user.id,
        name: [user.name, user.lastname].filter(Boolean).map(this.toLatin).join(" "),
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => this.toLatin(part).charAt(0))
          .join(""),
        meta: [`ID ${user.id}`, user.email].filter(Boolean).join(" · "),
      }));
    },
  },
  methods: {
    openUsers(row) {
      this.selectedRow = row;
      this.usersSheet = true;
    },
  },
};
</script>

<style lang="scss">
.rm-theme .gm-sheet-sub {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--rm-muted);
}

.rm-theme .gm-retry {
  width: auto;
  padding: 0 20px;
}

.gm-wk {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  &__meta {
    font-size: 12px;
    overflow-wrap: anywhere;
  }
}
</style>
